// @ts-strict-ignore
import { Box3, Vector3 } from 'three';
import { NumberUtils } from '@shared/util/number-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { Connection } from '@3d-scene/model3d/connection';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { PropertyUtils } from '@article/util/property-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { MetaElementUsageIdentityVO, MetaElementUsageVO, MetaElementVO, SimpleTextValueVO, TextureAreaUsageVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ContainerService } from '../container.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { UserVO } from '@user/model/vo/user.vo';
import { UserUtils } from '@user/service/user-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { TextCheckSpacingData } from '@3d-scene/manager/text-check-spacing-data';
import { FloatRangeVO } from '@project/components/article/model/vo/property.vo';
import { DefinedMetaElementInfoPropertyDef } from '@project/constants/defined-meta-element-info-property-def';
import { Dictionary } from 'typescript-collections';
import { ISpanData } from '@project/components/label/label-editor/span-data';
import { Injectable } from '@angular/core';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ConnectionInfoUtils } from './connection-info-utils';
import { ConnectionInfoService } from '../service/connection-info.service';
import { CorridorCalculationService } from './corridor-calculation.service';

@Injectable({ providedIn: 'root' })
export class LabelingUtils {
  public static readonly TYPE_IDS_IGNORED_BY_STRIP: number[] = [
    ArticleTypeIds.END_PLATE,
    ArticleTypeIds.MCS_GDS_END_PLATE,
    ArticleTypeIds.DRILLING_HOLE,
  ];
  public static readonly POINT_TO_SORT: Vector3 = new Vector3(10000, 0, 0);

  private static readonly USE_SPACING_FOR_LABELING_EXTRUSION: boolean = false;

  constructor(
    private _componentUtils: ContainerService,
    private _articleContainerUtils: ArticleContainerUtils,
    private connectionInfoService: ConnectionInfoService,
    private corridorCalculationService: CorridorCalculationService,
  ) {}

  public calculateAdapterLabelingRectangle(connection: Connection): Rectangle3D {
    const articleBounds = this.determineLabelLength(connection);

    const connectionRectangle: Rectangle3D = connection.compositionRectangle;

    const line1Min: Vector3 = MathUtils.closestPointOnLine(connectionRectangle.point1, connectionRectangle.point4, articleBounds.min);
    const line1Max: Vector3 = MathUtils.closestPointOnLine(connectionRectangle.point1, connectionRectangle.point4, articleBounds.max);
    const line2Min: Vector3 = MathUtils.closestPointOnLine(connectionRectangle.point2, connectionRectangle.point3, articleBounds.min);
    const line2Max: Vector3 = MathUtils.closestPointOnLine(connectionRectangle.point2, connectionRectangle.point3, articleBounds.max);

    let resultRectangle: Rectangle3D = null;
    if (line1Min.distanceTo(connectionRectangle.point1) < line1Max.distanceTo(connectionRectangle.point1)) {
      resultRectangle = Rectangle3D.fromArray([line1Min, line2Min, line2Max, line1Max]);
    } else {
      resultRectangle = Rectangle3D.fromArray([line1Max, line2Max, line2Min, line1Min]);
    }
    return resultRectangle;
  }

  private determineLabelLength(connection: Connection): Box3 {
    let articleBounds: Box3;
    const articleOnRail: ArticleContainer = this._articleContainerUtils.getBackwardArticleContainerOnRailRecursively(connection.articleContainer);
    const customLabelLength = PropertyUtils.getIntPropertyValue(
      articleOnRail.article.connectionFeatures,
      ArticleConnectionPropertyDef.ADAPTER_CUSTOM_LABEL_WIDTH
    );
    if (!Number.isNaN(customLabelLength)) {
      const adapter = connection.articleContainer;
      articleBounds = adapter.sceneTransformBounds;
      articleBounds.max.x += customLabelLength;
    } else {
      articleBounds = articleOnRail.sceneTransformBounds;
    }
    return articleBounds;
  }

  public calculateLabelingRectangle(connection: Connection): Rectangle3D {
    if (!connection || !connection.articleContainer) {
      return null;
    }

    let rectangle3D: Rectangle3D;
    const article: ArticleVO = connection.articleContainer.article;
    let labelCellWidth: number = UnitConverter.fromTo('mm', '3d', LabelingUtils.getLabelCellWidth(article, connection.id));
    if (LabelingUtils.USE_SPACING_FOR_LABELING_EXTRUSION && !NumberUtils.isNumber(labelCellWidth) && article) {
      labelCellWidth = UnitConverter.fromTo('mm', '3d', PropertyUtils.getFloatPropertyValue(article.connectionRequirements, ArticleConnectionPropertyDef.SPACING));
    }
    if (!NumberUtils.isNumber(labelCellWidth)) {
      rectangle3D = this.calculateRectangleWithCompression(connection);
    } else {
      rectangle3D = connection.compositionRectangle;
      if (!rectangle3D) {
        return null;
      }

      rectangle3D = rectangle3D.clone();
      this.interpolateRectangle(rectangle3D, labelCellWidth);
    }

    return rectangle3D;
  }

  private interpolateRectangle(rectangle: Rectangle3D, newWidth): void {
    if (MathUtils.compare(rectangle.width, newWidth) !== 0) {
      const offset: number = (rectangle.width - newWidth) / 2;
      MathUtils.interpolateRectangle(rectangle, offset, false);
      MathUtils.interpolateRectangle(rectangle, offset);
    }
  }

  public static getLabelCellWidth(article: ArticleVO, metaElementName: string): number {
    if (!article) {
      return NaN;
    }

    let cellWidth: number = NaN;
    const metaElement: MetaElementVO = ArticleUtils.findMetaElementByName(article, metaElementName);
    if (metaElement) {
      cellWidth = PropertyUtils.getNumberPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.LABEL_CELL_WIDTH);
    }
    return cellWidth;
  }

  /**
   * @return "compressed" rectangle by "common" rectangle calculated by connectionInfoUtils.calculateRectangleByConnection in case when multiple
   * connections are placed on an article in one location group.
   * "location group" here means a group of connection cubes, which have their own middle point placed inside calculated "common" rectangle.
   */
  private calculateRectangleWithCompression(connection: Connection): Rectangle3D {
    if (!connection) {
      return null;
    }

    const rectByConnection: Rectangle3D = this.connectionInfoService.calculateRectangleByConnection(connection);

    let connectionsWithSameRectangle: Connection[] = this._componentUtils.getConnectionsFromOneLocationGroup(
      connection.articleContainer,
      rectByConnection
    );
    const pointToSort: Vector3 = MathUtils.calculateMiddlePoint(rectByConnection.point1, rectByConnection.point2);
    connectionsWithSameRectangle = ConnectionInfoUtils.sortConnectionsByPoint(connectionsWithSameRectangle, pointToSort);
    const cubeIndex: number = connectionsWithSameRectangle.indexOf(connection);
    if (cubeIndex < 0) {
      return rectByConnection;
    }

    const offset: number = rectByConnection.width / connectionsWithSameRectangle.length;
    MathUtils.interpolateRectangle(rectByConnection, offset * cubeIndex, true);
    MathUtils.interpolateRectangle(rectByConnection, offset * (connectionsWithSameRectangle.length - 1 - cubeIndex), false);

    return rectByConnection;
  }

  public static createTextureMetaElementUsageIfNotExists(
    metaUsage: MetaElementUsageVO,
    articleGUID: string,
    metaElementVO: MetaElementVO,
    projectArticleService: ProjectArticleService,
    projectMetaElementService: ProjectMetaElementService,
    articleUsage?: ArticleUsageVO
  ): MetaElementUsageVO {
    if (!articleGUID || !metaElementVO || !projectArticleService.projectModel) {
      return null;
    }
    let usage: MetaElementUsageVO = metaUsage;
    if (!usage) {
      if (articleUsage) {
        usage = projectMetaElementService.addMetaElementUsageToArticleUsage(metaElementVO, articleUsage);
      } else {
        usage = projectMetaElementService.addMetaElementUsage(articleGUID, metaElementVO);
      }
    }
    if (usage instanceof TextureAreaUsageVO && !usage.textValue) {
      usage.textValue = new SimpleTextValueVO();
    }
    return usage;
  }

  public static applyTextureMetaElementUsageIfNotExists(
    connection: Connection,
    projectArticleService: ProjectArticleService,
    projectMetaElementService: ProjectMetaElementService,
    articleUsage?: ArticleUsageVO
  ): void {
    if (!connection || !connection.metaElementVO || !projectArticleService.projectModel) {
      return;
    }
    const articleContainer: ArticleContainer = connection.articleContainer;
    let usage: MetaElementUsageVO = articleContainer.findMetaElementUsageByName(connection.metaElementVO.name);
    usage = this.createTextureMetaElementUsageIfNotExists(
      usage,
      articleContainer.articleGUID,
      connection.metaElementVO,
      projectArticleService,
      projectMetaElementService,
      articleUsage
    );
    connection.metaElementUsage = usage;
  }

  public static getSpacingByArticle(article: ArticleVO, metaElementName?: string, useLabelCellWidth: boolean = true): number {
    let spacing: number = NaN;
    if (useLabelCellWidth) {
      spacing = this.getLabelCellWidth(article, metaElementName);
    }
    if (!NumberUtils.isNumber(spacing)) {
      spacing = PropertyUtils.getFloatPropertyValue(article.connectionRequirements, ArticleConnectionPropertyDef.SPACING);
    }
    return spacing;
  }

  public findMergedConnections(connectionCubes: Connection[]): Connection[] {
    if (!connectionCubes) {
      return [];
    }

    let result: Connection[] = [];
    for (const cube of connectionCubes) {
      result = result.concat(this.findNextMergedTextureConnections(cube));
    }
    return result;
  }

  public findAllMergedConnectionsByConnection(connection: Connection): Connection[] {
    if (!connection || !connection.metaElementUsage || !(connection.metaElementUsage instanceof TextureAreaUsageVO)) {
      return [];
    }

    return this.findNextMergedTextureConnections(connection)
      .concat(this.findPreviousMergedTextureConnections(connection));
  }

  /**
   * Returns merged texture connection cubes from given connection to last connection.
   * (it means that last texture area usage does have previous neighbor but doesn't have next neighbor).
   */
  public findNextMergedTextureConnections(connection: Connection): Connection[] {
    if (!connection || !connection.metaElementUsage || !(connection.metaElementUsage instanceof TextureAreaUsageVO)) {
      return [connection];
    }

    const mergedCubes: Connection[] = [connection];
    let nextConnection: Connection = this.findNextMergedConnection(connection);
    while (nextConnection) {
      mergedCubes.push(nextConnection);
      nextConnection = this.findNextMergedConnection(nextConnection);
    }

    return mergedCubes;
  }

  public findNextMergedConnection(startConnection: Connection): Connection {
    if (!startConnection || !startConnection.metaElementUsage || !(startConnection.metaElementUsage instanceof TextureAreaUsageVO)) {
      return null;
    }

    const textureUsage: TextureAreaUsageVO = startConnection.metaElementUsage;
    const nextTextureId: MetaElementUsageIdentityVO = textureUsage.nextMergedUsageId;
    if (!nextTextureId) {
      return null;
    }
    const articleContainer: ArticleContainer = this._componentUtils.findArticleContainerInGlobal(nextTextureId.articleUsageId);
    if (!articleContainer) {
      return null;
    }

    return articleContainer.findFirstConnectionByName(nextTextureId.metaElementName);
  }

  /**
   * In case of direct printing - texture area should be part of default labeling (no isNotPartOfDefaultLabeling-property).
   * In case of labeling article (e.g. WMB, strip) - labeling article should be connected to accepting connections which are part of
   * default labeling.
   */
  public isTextureCubePartOfDefaultLabeling(connection: Connection): boolean {
    if (!connection) {
      return false;
    }
    const articleContainer: ArticleContainer = connection.articleContainer;
    if (!articleContainer) {
      return false;
    }
    let isPartOfDefaultLabeling: boolean = false;
    if (ArticleUtils.isLabelingArticle(articleContainer.article)) {
      isPartOfDefaultLabeling = this.isLabelingArticleConnectedToDefaultLabelingConnections(articleContainer);
    } else {
      isPartOfDefaultLabeling = ConnectionInfoUtils.isPartOfDefaultLabeling(connection.metaElementVO);
    }
    return isPartOfDefaultLabeling;
  }

  public isLabelingArticleConnectedToDefaultLabelingConnections(articleContainer: ArticleContainer, excludedArticleIds: string[] = null): boolean {
    if (!articleContainer || !articleContainer.articleUsage) {
      return false;
    }
    const backwardConnectionIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(
      articleContainer.articleUsage
    );
    if (this.isArticleConnectedToExcludedArticle(excludedArticleIds, backwardConnectionIds)) {
      return true;
    }

    for (const parentAcceptingAreaId of backwardConnectionIds) {
      const acceptingConnectionCube: Connection = this.connectionInfoService.findConnectionByIdentity(parentAcceptingAreaId);
      if (!acceptingConnectionCube || !ConnectionInfoUtils.isPartOfDefaultLabeling(acceptingConnectionCube.metaElementVO)) {
        return false;
      }
    }
    return true;
  }

  private isArticleConnectedToExcludedArticle(excludedArticleIds: string[], backwardConnectionIds: MetaElementUsageIdentityVO[]): boolean {
    if (ArrayUtils.isEmpty(excludedArticleIds)) {
      return false;
    }
    for (const parentAcceptingAreaId of backwardConnectionIds) {
      if (excludedArticleIds.indexOf(parentAcceptingAreaId.articleUsageId) !== -1) {
        return true;
      }
    }
    return false;
  }

  public static isArticleIgnoredByLabelStrip(article: ArticleVO): boolean {
    return ArticleUtils.isArticleOfAnyTypeId(article, LabelingUtils.TYPE_IDS_IGNORED_BY_STRIP);
  }

  public userCanDeleteLabelingArticles(user: UserVO, articleContainers: ArticleContainer[]): boolean {
    if (!user || ArrayUtils.isEmpty(articleContainers)) {
      return false;
    }
    if (UserUtils.hasPermissionToEditAllLabelings(user)) {
      return true;
    }
    const articleIdsToDelete: string[] = ArticleContainerUtils.extractArticleGUIDs(articleContainers);
    for (const articleContainer of articleContainers) {
      if (
        ArticleUtils.isLabelingArticle(articleContainer.article) &&
        !this.isLabelingArticleConnectedToDefaultLabelingConnections(articleContainer, articleIdsToDelete)
      ) {
        return false;
      }
    }
    return true;
  }

  public static extractAdapterConnectionCubes(connectionCubes: Connection[]): Connection[] {
    const adapterConnectionCube: Connection[] = [];
    for (const connectionCube of connectionCubes) {
      if (ArticleUtils.isAdapterArticle(connectionCube.articleContainer.article)) {
        adapterConnectionCube.push(connectionCube);
      }
    }
    return adapterConnectionCube;
  }

  public static getTextCheckSpacingDataByTerminalArticle(article: ArticleVO): TextCheckSpacingData {
    return this.createTextCheckDataByArticle(article, null, false);
  }

  public getTextCheckSpacingDataByTextureCube(textureConnection: Connection): TextCheckSpacingData | null {
    if (!textureConnection) {
      return null;
    }
    let textCheckSpacingData: TextCheckSpacingData = null;
    const spacing: number = ArticleUsageUtils.getDefinedMetaElementInfoPropertyFloatValue(
      textureConnection.articleContainer.articleUsage,
      textureConnection.connectionName,
      DefinedMetaElementInfoPropertyDef.SPACING
    );
    if (NumberUtils.isNotNumber(spacing) || spacing === 0) {
      const articleContainer: ArticleContainer = textureConnection.articleContainer;
      if (articleContainer && textureConnection.metaElementVO instanceof TextureAreaVO) {
        if (ArticleUtils.isLabelingArticle(articleContainer.article)) {
          textCheckSpacingData = this.getTextCheckSpacingDataByLabelingTextureCube(textureConnection);
        } else {
          textCheckSpacingData = this.getTextCheckSpacingDataByConnection(textureConnection);
        }
      }
    } else {
      textCheckSpacingData = new TextCheckSpacingData(spacing, null);
    }
    return textCheckSpacingData;
  }

  private getTextCheckSpacingDataByLabelingTextureCube(textureConnection: Connection): TextCheckSpacingData {
    let textCheckSpacingData: TextCheckSpacingData = null;
    const articleContainer: ArticleContainer = textureConnection.articleContainer;
    const targetConnectionCube: Connection = this.getTargetSpacingConnectionByLabelingTexture(textureConnection);
    if (ArticleUtils.isDynamicArticle(articleContainer.article) && !ArticleUtils.isCoverLabelStripArticle(articleContainer.article)) {
      if (this.isArticleConnectedToAdapters(articleContainer)) {
        textCheckSpacingData = this.getAdapterLabelStripTextCheckSpacingData(targetConnectionCube);
      } else {
        textCheckSpacingData = this.getDefaultLabelStripTextCheckSpacingData(targetConnectionCube);
      }
    } else {
      textCheckSpacingData = this.getTextCheckSpacingDataByConnection(targetConnectionCube);
    }
    return textCheckSpacingData;
  }

  public isArticleConnectedToAdapters(articleContainer: ArticleContainer): boolean {
    const backwardIds: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleContainer.articleUsage);
    return this.isUsageIdsOfDifferentAdapters(backwardIds);
  }

  public isConnectionsOfDifferentAdapters(connections: Connection[]): boolean {
    return this.isUsageIdsOfDifferentAdapters(connections.map((connection) => connection.articleContainer.articleGUID));
  }

  private isUsageIdsOfDifferentAdapters(usageIds: string[]): boolean {
    const articleUsageIds = Array.from(new Set(usageIds));
    return articleUsageIds.length > 1 && this.isAdapterContainer(articleUsageIds[0]);
  }

  private isAdapterContainer(usageId: string): boolean {
    const container: ArticleContainer = this._componentUtils.findArticleContainerInGlobal(usageId);
    return container && ArticleUtils.isAdapterArticle(container.article);
  }

  public getTargetSpacingConnectionByLabelingTexture(textureConnection: Connection): Connection {
    let targetConnectionCube: Connection = this.findAcceptingConnectionCubeByRelativeConnection(
      textureConnection.metaElementVO as TextureAreaVO,
      textureConnection.articleContainer
    );
    if (!targetConnectionCube) {
      const backwardConnectionId: MetaElementUsageIdentityVO = ArticleUsageUtils.getFirstBackwardConnectedConnectionUsageId(
        textureConnection.articleContainer.articleUsage
      );
      if (backwardConnectionId) {
        targetConnectionCube = this.connectionInfoService.findConnectionByIdentity(backwardConnectionId);
      }
    }
    return targetConnectionCube;
  }

  public getTextCheckSpacingDataByConnection(connection: Connection): TextCheckSpacingData {
    return !connection ? null : this.createTextCheckDataByArticleContainer(connection.articleContainer, connection.connectionName, false);
  }

  public getDefaultLabelStripTextCheckSpacingData(connection: Connection): TextCheckSpacingData {
    return !connection ? null : this.createTextCheckDataByArticleContainer(connection.articleContainer, connection.connectionName, true);
  }

  public getAdapterLabelStripTextCheckSpacingData(connection: Connection): TextCheckSpacingData {
    return !connection ? null : this.createTextCheckDataByOnRailContainer(connection.articleContainer);
  }

  private createTextCheckDataByArticleContainer(
    articleContainer: ArticleContainer,
    metaElementName: string,
    useArticleOnRailAsFallback: boolean
  ): TextCheckSpacingData {
    if (!articleContainer) {
      return null;
    }
    let spacingData: TextCheckSpacingData = LabelingUtils.createTextCheckDataByArticle(articleContainer.article, metaElementName);
    if (!spacingData || (NumberUtils.isNotNumber(spacingData.spacing) && !spacingData.spacingInRange)) {
      if (useArticleOnRailAsFallback) {
        spacingData = this.createTextCheckDataByOnRailContainer(articleContainer);
      }
    }
    return spacingData;
  }

  private createTextCheckDataByOnRailContainer(articleContainer: ArticleContainer): TextCheckSpacingData {
    const articleContainerOnRail: ArticleContainer = this._articleContainerUtils.getBackwardArticleContainerOnRailRecursively(articleContainer);
    return !articleContainerOnRail ? null : LabelingUtils.createTextCheckDataByArticle(articleContainerOnRail.article, null, false);
  }

  private findAcceptingConnectionCubeByRelativeConnection(textureAreaVO: TextureAreaVO, container: ArticleContainer): Connection {
    if (!textureAreaVO || !container) {
      return null;
    }
    return this.connectionInfoService.findConnectionByIdentity(LabelingUtils.findAcceptingAreaIdByRelativeConnection(container, textureAreaVO.name));
  }

  public static findBackwardUsageIdByRelativeConnection(container: ArticleContainer, textureName: string): string {
    const acceptingAreaId: MetaElementUsageIdentityVO = this.findAcceptingAreaIdByRelativeConnection(container, textureName);
    if (!acceptingAreaId) {
      return null;
    }
    return acceptingAreaId.articleUsageId;
  }

  public static findAcceptingAreaIdByRelativeConnection(container: ArticleContainer, textureName: string): MetaElementUsageIdentityVO {
    if (!container) {
      return null;
    }
    return this.findAcceptingAreaIdByRelativeConnectionByUsage(container.articleUsage, textureName);
  }

  public static findAcceptingAreaIdByRelativeConnectionByUsage(articleUsage: ArticleUsageVO, textureName: string) {
    if (!textureName || !articleUsage) {
      return null;
    }
    const connectionName: string = textureName.replace(MetaElementVO.TEXTURE_PREFIX, MetaElementVO.CONNECTION_PREFIX);
    return ArticleUsageUtils.getAcceptingAreaIdByConnectionName(articleUsage, connectionName);
  }

  public static createTextCheckDataByArticle(article: ArticleVO, metaElementName: string, useLabelCellWidth: boolean = true): TextCheckSpacingData {
    if (!article) {
      return null;
    }
    let spacing: number = this.getSpacingByArticle(article, metaElementName, useLabelCellWidth);
    let spacingInRange: FloatRangeVO;
    let spacingData: TextCheckSpacingData;
    if (NumberUtils.isNotNumber(spacing) || spacing === 0) {
      spacingInRange = PropertyUtils.getFloatRangePropertyValue(article.connectionRequirements, ArticleConnectionPropertyDef.SPACING);
      spacing = NaN;
    }
    spacingData = new TextCheckSpacingData(spacing, spacingInRange);
    return spacingData;
  }

  public findFirstMergedTextureCube(connection: Connection): Connection {
    if (
      !connection ||
      !connection.metaElementUsage ||
      !(connection.metaElementUsage instanceof TextureAreaUsageVO) ||
      !connection.metaElementUsage.previousMergedUsageId
    ) {
      return connection;
    }
    let previousConnection: Connection = this.findPreviousMergedConnection(connection);
    const previousConnections: Connection[] = [];
    while (previousConnection) {
      previousConnections.push(previousConnection);
      previousConnection = this.findPreviousMergedConnection(previousConnection);
    }

    return previousConnections[previousConnections.length - 1];
  }

  /**
   * Returns merged texture connection cubes from given connection to first connection.
   * (it means that last texture area usage does have next neighbor but doesn't have previous neighbor).
   */
  public findPreviousMergedTextureConnections(connection: Connection): Connection[] {
    if (!connection || !connection.metaElementUsage || !(connection.metaElementUsage instanceof TextureAreaUsageVO)) {
      return [connection];
    }

    const mergedCubes: Connection[] = [connection];
    let nextConnection: Connection = this.findPreviousMergedConnection(connection);
    while (nextConnection) {
      mergedCubes.push(nextConnection);
      nextConnection = this.findPreviousMergedConnection(nextConnection);
    }

    return mergedCubes;
  }

  public findPreviousMergedConnection(startConnection: Connection): Connection {
    const previousTextureId: MetaElementUsageIdentityVO = this.getPreviousMergedConnectionId(startConnection);
    if (!previousTextureId) {
      return null;
    }
    const articleContainer: ArticleContainer = this._componentUtils.findArticleContainerInGlobal(previousTextureId.articleUsageId);
    if (!articleContainer) {
      return null;
    }
    return articleContainer.findFirstConnectionByName(previousTextureId.metaElementName);
  }

  public getPreviousMergedConnectionId(connection: Connection): MetaElementUsageIdentityVO {
    if (!connection || !connection.metaElementUsage || !(connection.metaElementUsage instanceof TextureAreaUsageVO)) {
      return null;
    }
    return connection.metaElementUsage.previousMergedUsageId;
  }

  public static calculateAverageSpacing(spanDatas: ISpanData[]): number {
    let mergedSpacing: number = 0;
    for (const spanData of spanDatas) {
      if (NumberUtils.isNumber(spanData.spacing)) {
        mergedSpacing += spanData.spacing;
      }

      if (spanDatas.indexOf(spanData) !== spanDatas.length - 1) {
        if (NumberUtils.isNumber(spanData.spacingToNextSpanDataInRow)) {
          mergedSpacing += spanData.spacingToNextSpanDataInRow;
        }
      }
    }
    return mergedSpacing / spanDatas.length;
  }

  /**
   * Provides corridor numbers that are contained in given connection cubes with text connection type.
   * @param connections given array of connection cubes
   * @return array of corridor positions
   *
   */
  public getCorridorsByConnectionCubes(connections: Connection[]): number[] {
    const corridors: number[] = [];
    for (const connection of connections) {
      const corridorPosition: number = connection && connection.isTextureArea() ? this.getTextureConnectionCorridor(connection) : NaN;
      if (NumberUtils.isNumber(corridorPosition) && corridors.indexOf(corridorPosition) === -1) {
        corridors.push(corridorPosition);
      }
    }
    corridors.sort((c1, c2) => NumberUtils.numericCompare(c1, c2));
    corridors.reverse();
    return corridors;
  }

  /**
   * Returns so called texture corridor of connection.
   * If given texture connection does not have corridor information, then a corridor information is determined by using parent article(s).
   */
  public getTextureConnectionCorridor(connection: Connection): number {
    return this.corridorCalculationService.calculateCorridorValue(connection);
  }

  /**
   * Returns connection cubes, which are located in the provided corridor.
   *
   * @param connections given connection cubes
   * @param corridor connection corridor
   * @param excludeMergedConnections exclude connections which are merged with another connection (previousMergedUsageId is not null)
   * @return connection cubes in the corridor
   *
   */
  public getConnectionCubesInCorridor(connections: Connection[], corridor: number, excludeMergedConnections: boolean = true): Connection[] {
    if (NumberUtils.isNotNumber(corridor)) {
      return [];
    }
    const result: Connection[] = [];
    for (const connection of connections) {
      const connectionCorridor: number = connection && connection.isTextureArea() ? this.getTextureConnectionCorridor(connection) : NaN;
      if (corridor !== connectionCorridor) {
        continue;
      }
      if (!excludeMergedConnections || !this.getPreviousMergedConnectionId(connection)) {
        result.push(connection);
      }
    }
    return this.getSortedConnectionCubesAccordingToArticles(result);
  }

  private getSortedConnectionCubesAccordingToArticles(connectionCubes: Connection[]): Connection[] {
    if (connectionCubes.length < 2) {
      return connectionCubes;
    }

    const cubesOfArticles: Dictionary<string, Connection[]> = new Dictionary<string, Connection[]>();
    // store cubes of articles
    for (const cube of connectionCubes) {
      const currentArticle: ArticleContainer = this._componentUtils.getArticleContainer(cube);
      let currentArticleGroup: Connection[] = cubesOfArticles.getValue(currentArticle.articleGUID);
      if (!currentArticleGroup) {
        currentArticleGroup = [];
      }
      currentArticleGroup.push(cube);
      cubesOfArticles.setValue(currentArticle.articleGUID, currentArticleGroup);
    }

    let firstConnectionCubeFromArticles: Connection[] = [];
    const sortedCubesOfArticles: Dictionary<string, Connection[]> = new Dictionary<string, Connection[]>();
    // sort for each article
    for (const connectionCubes of cubesOfArticles.values()) {
      const sortedConnectionCubes: Connection[] = ConnectionInfoUtils.sortConnections(connectionCubes);
      const firstConnectionCube: Connection = sortedConnectionCubes[0];
      firstConnectionCubeFromArticles.push(firstConnectionCube);
      sortedCubesOfArticles.setValue(firstConnectionCube.articleContainer.articleGUID, sortedConnectionCubes);
    }
    firstConnectionCubeFromArticles = ConnectionInfoUtils.sortConnectionsByPoint(firstConnectionCubeFromArticles, LabelingUtils.POINT_TO_SORT);

    return this.groupAndSortByVDirection(firstConnectionCubeFromArticles, sortedCubesOfArticles);
  }

  // group article containers by V-direction on texture connections and than sort by V-direction (negative V for non labeling articles)
  // to write text and merge connectons by V-direction (negative V for non labeling articles)
  private groupAndSortByVDirection(
    firstConnectionCubeFromArticles: Connection[],
    sortedCubesOfArticles: Dictionary<string, Connection[]>
  ): Connection[] {
    if (ArrayUtils.isEmpty(firstConnectionCubeFromArticles) || sortedCubesOfArticles.isEmpty()) {
      return [];
    }
    const groupsByVDirection: { reverse: boolean; connections: Connection[] }[] = [];
    let previousVDirection: Vector3 = null;
    for (const firstCube of firstConnectionCubeFromArticles) {
      const vDirection: Vector3 = ConnectionInfoUtils.getConnectionVDirection(firstCube);
      if (MathUtils.equalsNumber3D(previousVDirection, vDirection)) {
        groupsByVDirection[groupsByVDirection.length - 1].connections.push(firstCube);
      } else {
        groupsByVDirection.push({
          reverse: !(ArticleUtils.isDynamicLabelingArticle(firstCube.article) && !ArticleUtils.isCoverLabelStripArticle(firstCube.article)),
          connections: [firstCube],
        });
      }
      previousVDirection = vDirection;
    }

    let result: Connection[] = [];
    for (const group of groupsByVDirection) {
      let groupedCubes: Connection[] = [];
      for (const firstCube of group.connections) {
        const cubesOfArticle: Connection[] = sortedCubesOfArticles.getValue(firstCube.articleContainer.articleGUID);
        ArrayUtils.mergeArrays(groupedCubes, cubesOfArticle);
      }
      groupedCubes = ConnectionInfoUtils.sortConnections(groupedCubes);
      if (group.reverse) {
        groupedCubes = groupedCubes.reverse();
      }
      result = result.concat(groupedCubes);
    }
    return result;
  }

  public static getMaxAllowedMultilines(connectionCubes: Connection[]): number {
    let maxMultilinePresent: number = 1;
    for (const connectionCube of connectionCubes) {
      const allowedMultilines: number = PropertyUtils.getIntPropertyValue(
        connectionCube.metaElementVO.behaviorProperties,
        MetaElementBehaviorPropertyDef.ALLOWED_MULTILINES
      );
      if (NumberUtils.isNumber(allowedMultilines) && allowedMultilines > maxMultilinePresent) {
        maxMultilinePresent = allowedMultilines;
      }
    }
    return maxMultilinePresent;
  }

  public collectAllMergedTextureConnections(connections: Connection[]): Connection[] {
    if (!connections || !connections.length) {
      return [];
    }

    return ArrayUtils.distinct(connections.concat(connections
      .filter(connection => connection.metaElementUsage instanceof TextureAreaUsageVO)
      .flatMap(connection => this.findAllMergedConnectionsByConnection(connection))));
  }
}
